import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { User } from '@auth0/auth0-react';

import GET_ORGANIZATION, {
  GetOrganization,
  GetOrganizationVariables,
} from '../../graphql/queries/get-organization';

export type SettingsMenuItem = {
  key: string;
  text: string;
  icon: React.ReactNode;
  action: () => void | Promise<void>;
  // Defaults to true; set false for items whose icon shows in-flight feedback (e.g. logout's spinner) so the menu stays open
  closesMenu?: boolean;
};

interface SettingsMenuProps {
  items: SettingsMenuItem[];
  user?: User;
  loading?: boolean;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  items,
  user = undefined,
  loading = false,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'settings-menu' });
  const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null);
  const [ logoLoadFailed, setLogoLoadFailed ] = useState(false);

  const { data, loading: orgLoading } = useQuery<GetOrganization, GetOrganizationVariables>(
    GET_ORGANIZATION,
    {
      variables: { id: user?.org_id ?? '' },
      skip: !user?.org_id || Boolean(loading),
    },
  );

  const logoUrl = data?.getOrganization?.branding?.logo_url;

  // Allow a retry when the logo URL or org changes after a failed load
  React.useEffect(() => {
    setLogoLoadFailed(false);
  }, [ logoUrl, user?.org_id ]);

  // https only — an http logo would be mixed-content blocked; anything else falls back to the user avatar
  const showOrgLogo = Boolean(logoUrl?.startsWith('https://')) && !logoLoadFailed;

  // Until auth and the org query settle we don't know whether to show a logo or the
  // user avatar; render a neutral skeleton so the trigger lands once instead of
  // flashing the user avatar and then swapping to the org logo.
  const isResolving = Boolean(loading) || orgLoading;

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ flexShrink: 1 }}>
      <Tooltip title={t('open_settings_tooltip')}>
        <IconButton
          sx={{ p: 0 }}
          onClick={openMenu}
        >
          {isResolving && (
            <Skeleton
              height={40}
              variant='circular'
              width={40}
            />
          )}
          {!isResolving && showOrgLogo && (
            // objectFit: cover so the logo fills the circular avatar to match the user-avatar
            // footprint; logos wider or taller than square are center-cropped to fit
            <Avatar
              alt={data?.getOrganization?.display_name || data?.getOrganization?.name}
              slotProps={{
                img: {
                  style: { objectFit: 'cover' },
                  onError: () => setLogoLoadFailed(true),
                },
              }}
              src={logoUrl}
            />
          )}
          {!isResolving && !showOrgLogo && (
            <Avatar
              alt={user?.name}
              src={user?.picture}
            />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id='menu-appbar'
        open={Boolean(anchorEl)}
        sx={{ mt: '45px' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={closeMenu}
      >
        <ListSubheader
          disableSticky
          sx={{
            color: 'text.secondary',
            lineHeight: '36px',
          }}
        >
          {t('logged_in_as', { name: user?.name || user?.email || '' })}
        </ListSubheader>
        <Divider />
        {items.map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => {
              if (item.closesMenu !== false) {
                closeMenu();
              }
              item.action();
            }}
          >
            {item.icon}
            <Typography sx={{
              paddingLeft: 1,
              textAlign: 'center',
            }}
            >
              {item.text}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default SettingsMenu;
